/**
 * WhyChooseUs - Redesigned to remove emojis and apply refined corporate grid layouts
 */
import React from 'react';
import { motion } from 'framer-motion';
import { FaShip, FaBoxes, FaNetworkWired, FaTools, FaShieldAlt, FaLeaf } from 'react-icons/fa';
import { StaggerContainer, StaggerItem } from '../ui/AnimatedSection';
import SectionHeader from '../ui/SectionHeader';

const features = [
  { icon: FaShip, title: 'Maritime Engineering Expertise', desc: 'Decades of seasoned agency handling, husbanding, container, and tanker management.' },
  { icon: FaBoxes, title: 'End-to-End Logistical Chains', desc: 'Tailored transport, CFS forwarding, warehousing, and custom clearances.' },
  { icon: FaNetworkWired, title: 'Global Port Connections', desc: 'Strategic relationships with vessel principals, port administrators and operators.' },
  { icon: FaTools, title: 'Uncompromising Service Delivery', desc: 'Operating with strict punctuality and adaptive scheduling for key routes.' },
  { icon: FaShieldAlt, title: 'Statutory Safety & Compliance', desc: 'Adhering strictly to international compliance codes and environmental mandates.' },
  { icon: FaLeaf, title: 'Pioneering Green Energy', desc: 'Exclusive partnership with Hissen Engineering to introduce net zero solutions.' }
];

const highlights = [
  { label: 'Subcontinent Coverage' },
  { label: 'Global Alliance System' },
  { label: '24/7 Operations Desk' },
  { label: 'Client-Centric Charter' }
];

const WhyChooseUs = () => {
  return (
    <section style={{
      padding: '4.5rem 0',
      background: 'var(--bg-page)',
      overflow: 'hidden',
      borderBottom: '1px solid rgba(10,17,40,0.05)'
    }}>
      <div className="container-xxl px-4 px-lg-5">

        <SectionHeader
          tag="Our Value Proposition"
          title="Why Partners Select ISAAC"
          subtitle="Combining regulatory trust, specialized marine engineering, and logistics networks to keep global trade moving."
        />

        {/* Typographic highlights (Replacing emoji pills) */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '1rem',
          marginBottom: '4.5rem'
        }}>
          {highlights.map((h, i) => (
            <div
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: '#FFFFFF',
                border: '1px solid rgba(10,17,40,0.06)',
                borderRadius: 2,
                padding: '0.45rem 1.2rem',
                boxShadow: 'var(--shadow-sm)',
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '0.72rem',
                color: 'var(--navy)',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}
            >
              <span style={{ color: 'var(--orange)', marginRight: '0.5rem' }}>{"//"}</span> {h.label}
            </div>
          ))}
        </div>

        {/* Feature Grid */}
        <StaggerContainer className="row g-4" staggerDelay={0.05}>
          {features.map((f, i) => {
            const IconComp = f.icon;
            return (
              <StaggerItem key={i} className="col-lg-4 col-md-6">
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.25 }}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 2,
                    padding: '2.5rem 2rem',
                    height: '100%',
                    border: '1px solid rgba(10,17,40,0.04)',
                    boxShadow: 'var(--shadow-sm)',
                    transition: 'border-color 0.2s ease'
                  }}
                  className="why-choose-card"
                >
                  <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: 2,
                    background: 'var(--orange-dim)',
                    border: '1px solid var(--orange-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <IconComp size={14} color="var(--orange)" />
                  </div>
                  
                  <h5 style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    color: 'var(--navy)',
                    fontSize: '0.98rem',
                    marginBottom: '0.75rem',
                    letterSpacing: '-0.015em'
                  }}>
                    {f.title}
                  </h5>
                  
                  <p style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-body)',
                    lineHeight: 1.7,
                    margin: 0
                  }}>
                    {f.desc}
                  </p>
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>

      <style>{`
        .why-choose-card:hover {
          border-color: var(--orange) !important;
        }
      `}</style>
    </section>
  );
};

export default WhyChooseUs;
